import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';

// Environment variables validation (removed debug logging for security)

// Validate required environment variables
const clientId = process.env.REACT_APP_ENTRA_CLIENT_ID;
const tenantId = process.env.REACT_APP_ENTRA_TENANT_ID;
const redirectUri = process.env.REACT_APP_ENTRA_REDIRECT_URI || `${window.location.origin}/login-entra`;

if (!clientId) {
    console.error('❌ REACT_APP_ENTRA_CLIENT_ID is not set!');
}
if (!tenantId) {
    console.error('❌ REACT_APP_ENTRA_TENANT_ID is not set!');
}
if (!redirectUri) {
    console.error('❌ REACT_APP_ENTRA_REDIRECT_URI is not set!');
}

// MSAL Configuration
const msalConfig = {
    auth: {
        clientId: clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: redirectUri,
        knownAuthorities: [`login.microsoftonline.com`],
        navigateToLoginRequestUrl: false, // Prevent automatic navigation
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true, // Enable cookie storage for better cross-domain support
    },
    system: {
        allowRedirectInIframe: false,
        allowNativeBroker: false,
        windowHashTimeout: 90000, // Increase timeout for better reliability
        iframeHashTimeout: 10000, // Increase iframe timeout
        loadFrameTimeout: 0, // Disable iframe loading
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                // Only log errors in production, suppress all other logs
                if (process.env.NODE_ENV === 'production') {
                    if (level === 0) { // Error only
                        console.error('MSAL Error:', message);
                    }
                    return;
                }
                // In development, log everything
                switch (level) {
                    case 0: // Error
                        console.error('MSAL Error:', message);
                        break;
                    case 1: // Warning
                        console.warn('MSAL Warning:', message);
                        break;
                    case 2: // Info
                        console.info('MSAL Info:', message);
                        break;
                    case 3: // Verbose
                        console.debug('MSAL Verbose:', message);
                        break;
                    default:
                        console.log('MSAL Log:', message);
                }
            },
            piiLoggingEnabled: false,
            logLevel: process.env.NODE_ENV === 'production' ? 0 : 2, // Error only in prod, Info in dev
        },
    },
};

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Login request
const loginRequest = {
    scopes: ['User.Read', 'openid', 'profile', 'email'],
};

// Auth Context
const EntraAuthContext = createContext();

// Custom hook to use auth context
export const useEntraAuth = () => {
    const context = useContext(EntraAuthContext);
    if (!context) {
        throw new Error('useEntraAuth must be used within an EntraAuthProvider');
    }
    return context;
};

// Auth Provider Component
export const EntraAuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Inactivity timeout configuration - 10 minutes (600,000 milliseconds)
    const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
    const inactivityTimerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    // Logout function
    const logout = useCallback(async (redirectUri = null) => {
        try {
            // Clear local authentication state first
            setIsAuthenticated(false);
            setUser(null);

            // Clear all local storage and session storage
            localStorage.clear();
            sessionStorage.clear();

            // Get the account to construct logout URL
            const account = msalInstance.getAllAccounts()[0];

            if (account) {
                // Build logout URL manually to avoid account picker
                const logoutUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout`;
                const params = new URLSearchParams();

                // Add post logout redirect URI
                if (redirectUri) {
                    params.append('post_logout_redirect_uri', redirectUri);
                }

                // Redirect to logout URL
                window.location.href = `${logoutUrl}?${params.toString()}`;
            } else {
                // No account, just redirect
                if (redirectUri) {
                    window.location.href = redirectUri;
                }
            }
        } catch (error) {
            console.error('Logout error:', error);
            // Fallback: just redirect
            if (redirectUri) {
                window.location.href = redirectUri;
            }
        }
    }, []);

    // Handle inactivity timeout
    const handleInactivityTimeout = useCallback(() => {
        // Redirect to main website after inactivity logout
        logout('https://www.evoomedical.ro');
    }, [logout]);

    // Reset inactivity timer
    const resetInactivityTimer = useCallback(() => {
        // Clear existing timer
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }

        // Update last activity timestamp
        lastActivityRef.current = Date.now();

        // Set new timer
        inactivityTimerRef.current = setTimeout(() => {
            handleInactivityTimeout();
        }, INACTIVITY_TIMEOUT);
    }, [INACTIVITY_TIMEOUT, handleInactivityTimeout]);

    // Set up inactivity tracking
    useEffect(() => {
        // Only track activity if user is authenticated
        if (!isAuthenticated) {
            return;
        }

        // Activity event handlers
        const activityEvents = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click',
        ];

        // Reset timer on any activity
        const handleActivity = () => {
            resetInactivityTimer();
        };

        // Add event listeners
        activityEvents.forEach(event => {
            document.addEventListener(event, handleActivity, true);
        });

        // Start initial timer
        resetInactivityTimer();

        // Cleanup function
        return () => {
            // Remove event listeners
            activityEvents.forEach(event => {
                document.removeEventListener(event, handleActivity, true);
            });

            // Clear timer
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }
        };
    }, [isAuthenticated, resetInactivityTimer]);

    // Initialize MSAL
    useEffect(() => {
        const initializeMsal = async () => {
            try {
                // Initialize MSAL instance
                await msalInstance.initialize();

                // Handle redirect promise
                const response = await msalInstance.handleRedirectPromise();

                // If we got a response, user just authenticated
                if (response) {
                    const userEmail = response.account.username || response.account.email || '';

                    setIsAuthenticated(true);
                    setUser({
                        name: response.account.name,
                        username: response.account.username,
                        email: userEmail,
                        localAccountId: response.account.localAccountId,
                        entraRoles: response.idTokenClaims?.roles || [],
                    });

                    // Clear the Microsoft overlay since authentication is complete
                    if (window.clearRedirectOverlay) {
                        window.clearRedirectOverlay();
                    }

                    // Check if user has @evoomedical.ro email and redirect accordingly
                    if (userEmail && userEmail.includes('@evoomedical.ro')) {
                        const compasUrl = process.env.REACT_APP_COMPAS_URL || 'https://compas.evoomedical.ro';
                        setTimeout(() => {
                            window.location.href = `${compasUrl}?autoLogin=true`;
                        }, 1500);
                    }
                    return;
                }

                // Check if user is already signed in
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    setIsAuthenticated(true);
                    setUser({
                        name: accounts[0].name,
                        username: accounts[0].username,
                        localAccountId: accounts[0].localAccountId,
                        entraRoles: [], // Will be populated from token claims
                    });
                }
            } catch (error) {
                console.error('MSAL initialization error:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        initializeMsal();

        // Listen for auth events
        const callbackId = msalInstance.addEventCallback((event) => {
            if (event.eventType === EventType.LOGIN_SUCCESS) {
                const account = event.payload.account;
                setIsAuthenticated(true);
                setUser({
                    name: account.name,
                    username: account.username,
                    localAccountId: account.localAccountId,
                    entraRoles: event.payload.idTokenClaims?.roles || [],
                });
                setError(null);

                // Clear the Microsoft overlay since authentication is complete
                if (window.clearRedirectOverlay) {
                    window.clearRedirectOverlay();
                }
            } else if (event.eventType === EventType.LOGIN_FAILURE) {
                setError(event.error?.message || 'Login failed');
            } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
                setIsAuthenticated(false);
                setUser(null);
                setError(null);
            }
        });

        return () => {
            if (callbackId) {
                msalInstance.removeEventCallback(callbackId);
            }
        };
    }, []);

    // Login function
    const login = async (loginHint = null) => {
        try {
            setError(null);
            const request = { ...loginRequest };

            if (loginHint) {
                request.loginHint = loginHint;
                // Add domain_hint for evoomedical.ro accounts to auto-select the account
                if (loginHint.includes('@evoomedical.ro')) {
                    request.domain_hint = 'evoomedical.ro';
                }
                // Force login prompt to use the specified loginHint instead of showing account picker
                request.prompt = 'login';
            }

            // Use redirect approach which is more reliable than popup
            // The overlay should be shown by the calling component before calling this function
            await msalInstance.loginRedirect(request);
        } catch (error) {
            console.error('Login error:', error);
            setError(error.message);
        }
    };

    // Get access token
    const getAccessToken = async () => {
        try {
            const account = msalInstance.getAllAccounts()[0];
            if (!account) {
                throw new Error('No account found');
            }

            const tokenResponse = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account,
            });

            return tokenResponse.accessToken;
        } catch (error) {
            console.error('Token acquisition error:', error);
            // Try interactive token acquisition
            try {
                const tokenResponse = await msalInstance.acquireTokenRedirect(loginRequest);
                return tokenResponse.accessToken;
            } catch (interactiveError) {
                throw interactiveError;
            }
        }
    };

    const value = {
        isAuthenticated,
        user,
        loading,
        error,
        login,
        logout,
        getAccessToken,
        msalInstance,
    };

    return (
        <EntraAuthContext.Provider value={value}>
            {children}
        </EntraAuthContext.Provider>
    );
};
